import { useEffect, useState } from "react";
import { Edit, Trash2, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const MEMBER_STATUSES = ["Active", "Away", "Inactive"];

export const TeamMemberManager = ({ open, onClose, members, users = [], onCreate, onUpdate, onDelete }) => {
  const [editingMember, setEditingMember] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    email: "",
    status: "Active",
    user_id: "",
  });

  useEffect(() => {
    if (!editingMember) {
      setFormData({ name: "", role: "", email: "", status: "Active", user_id: "" });
      return;
    }
    setFormData({
      name: editingMember.name || "",
      role: editingMember.role || "",
      email: editingMember.email || "",
      status: editingMember.status || "Active",
      user_id: editingMember.user_id || "",
    });
  }, [editingMember, open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name.trim()) return;
    setSubmitting(true);
    try {
      if (editingMember) {
        await onUpdate(editingMember.id, {
          ...formData,
          name: formData.name.trim(),
          role: formData.role.trim(),
          email: formData.email.trim(),
        });
      } else {
        await onCreate({
          ...formData,
          name: formData.name.trim(),
          role: formData.role.trim(),
          email: formData.email.trim(),
        });
      }
      setEditingMember(null);
      setFormData({ name: "", role: "", email: "", status: "Active", user_id: "" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">
            Team Members
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.9fr)] gap-6 mt-4">
          <div className="border border-zinc-200">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200 bg-zinc-50">
              <Users className="text-zinc-500" size={16} />
              <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">Current Team</h3>
            </div>
            <div className="divide-y divide-zinc-200 max-h-[420px] overflow-y-auto">
              {members.length === 0 ? (
                <div className="p-6 text-sm text-zinc-400">No team members yet.</div>
              ) : (
                members.map((member) => (
                  <div className="flex items-start justify-between gap-4 p-4" key={member.id}>
                    <div className="min-w-0">
                      <p className="font-semibold text-zinc-900">{member.name}</p>
                      <p className="text-sm text-zinc-500">{member.role || "No role set"}</p>
                      <p className="text-sm text-zinc-400">{member.email || "No email"}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                        {member.status}
                      </span>
                      <button
                        className="text-zinc-400 hover:text-zinc-700"
                        onClick={() => setEditingMember(member)}
                        type="button"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="text-zinc-400 hover:text-red-600"
                        onClick={() => onDelete(member.id)}
                        type="button"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <form className="space-y-4 border border-zinc-200 p-5 bg-zinc-50/40" onSubmit={handleSubmit}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
              {editingMember ? "Edit Team Member" : "Add Team Member"}
            </h3>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Linked Login</Label>
              <Select onValueChange={(value) => setFormData({ ...formData, user_id: value === "__none__" ? "" : value })} value={formData.user_id || "__none__"}>
                <SelectTrigger className="rounded-none border-zinc-300 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">No login linked</SelectItem>{users.filter((user) => user.status !== "Inactive").map((user) => <SelectItem key={user.id} value={user.id}>{user.display_name || user.username} · {user.role}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-zinc-400">Links assignments and reminders to an actual system user.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Name *</Label>
              <Input
                className="rounded-none border-zinc-300 bg-white"
                onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                placeholder="Unabatsho Sitale"
                required
                value={formData.name}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Role</Label>
              <Input
                className="rounded-none border-zinc-300 bg-white"
                onChange={(event) => setFormData({ ...formData, role: event.target.value })}
                placeholder="Project Manager"
                value={formData.role}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Email</Label>
              <Input
                className="rounded-none border-zinc-300 bg-white"
                onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                placeholder="name@batsho.com"
                type="email"
                value={formData.email}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Status</Label>
              <Select onValueChange={(value) => setFormData({ ...formData, status: value })} value={formData.status}>
                <SelectTrigger className="rounded-none border-zinc-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEMBER_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap justify-end gap-3 pt-3">
              {editingMember ? (
                <Button
                  className="rounded-none border-zinc-300"
                  onClick={() => setEditingMember(null)}
                  type="button"
                  variant="outline"
                >
                  Cancel Edit
                </Button>
              ) : null}
              <Button
                className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-none px-6 text-xs font-bold uppercase tracking-wider"
                disabled={submitting}
                type="submit"
              >
                {submitting ? "Saving..." : editingMember ? "Update Member" : "Add Member"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
